import React, { useCallback, useContext, useEffect, useMemo } from "react";
import { useFormikContext } from "formik";
import _ from "lodash";
import { Flex } from "reflexbox";
import { Field } from "../../UI/forms";
import Button from "../../UI/Button";
import { useTheme } from "styled-components";
import ConfiguratorContext from "../../../context/configurator/ConfiguratorContext";
import * as r from "../../../requests/addressManagementRequests";
import AlertContext from "../../../context/alert/AlertContext";

const { createAddress } = r;

const CreateShippingAddress = ({
	formToShow,
	setFormToShow,
	setEditingId,
	updateQuote,
	refetchQuote,
	refetchCustomer,
	setFieldValue
}) => {
	const theme = useTheme();
	const { values } = useFormikContext();
	const { quote, setQuote } = useContext(ConfiguratorContext);
	const { showFailureAlert } = useContext(AlertContext);

	const shippingStateCodes = useMemo(
		() =>
			_.filter(
				quote?.stateCodes || [],
				({ country }) => country === values?.address?.Country
			),
		[quote.stateCodes, values]
	);

	const stateOptions = useMemo(
		() =>
			shippingStateCodes.map(({ state, description, country }) => (
				<option value={state} key={`${country}_${state}`}>
					{description}
				</option>
			)),
		[shippingStateCodes]
	);

	const countryOptions = useMemo(
		() =>
			quote?.countryCodes?.map(({ country, iso_country_code }) => (
				<option value={country} key={iso_country_code}>
					{country}
				</option>
			)),
		[quote]
	);

	const onSubmitAddress = useCallback(
		async (values) => {
			if (!values?.Name) {
				showFailureAlert("Name is required.");
				return;
			}
			if (!values?.Address) {
				showFailureAlert("Address is required.");
				return;
			}
			if (!values?.City) {
				showFailureAlert("City is required.");
				return;
			}
			if (!values?.Country) {
				showFailureAlert("Country is required.");
				return;
			}

			if (!values?.Zip) {
				showFailureAlert("Zip is required.");
				return;
			}

			// checking is the current state value is valid for the selected country
			const stateValidation = stateOptions.find((state) => state?.props?.value === values?.State);

			console.log({stateValidation});
			// Need to clear the state value if the country was changed from a country with states to one without them
			if (stateOptions.length === 0 || !stateValidation)
				values.State = null;

			const newAddress = await createAddress({
				input: {
					...values,
					AddressType: 2,
					customerId: quote?.customer?.id,
				},
			});
			let _quote = { ...quote };

			const adds = [...quote?.shippingAddresses];
			adds.push(newAddress);
			_quote.shippingAddresses = [...adds];
			_quote.shippingAddress = newAddress;
			_quote.shippingAddressId = newAddress.id;
			setFormToShow("");
			setEditingId(null);
			setQuote(_quote);

			await updateQuote({
				id: _quote.id,
				input: { shippingAddressId: newAddress.id },
			});

			refetchQuote();
			refetchCustomer();
			return;
		},
		[stateOptions, quote, setFormToShow, setEditingId, setQuote, updateQuote, refetchQuote, refetchCustomer, showFailureAlert]
	);

	const handleKeys = useCallback(
		(e) => {
			if ((e.code === "Escape" || e.keyCode === 27) && !!formToShow) {
				setFormToShow("");
				setEditingId(null);
			}
			if (e.key === "Enter" && !!formToShow) {
				onSubmitAddress(values.address);
				setFormToShow("");
				setEditingId(null);
			}
		},
		[formToShow, onSubmitAddress, setEditingId, setFormToShow, values.address]
	);

	useEffect(() => {
		if (formToShow) {
			document.addEventListener("keydown", (e) => handleKeys(e));
			return () => {
				window.removeEventListener("keydown", (e) => handleKeys(e));
			};
		}
	}, [handleKeys, formToShow]);

	return (
		<Flex flexDirection="column" m="0 auto" maxWidth="500px" w={1}>
			<Field name="address.Name" labelText="Name" required />
			<Field name="address.Address" labelText="Address Line 1" required />
			<Field name="address.Address2" labelText="Address Line 2" />
			<Field name="address.City" labelText="City" required />
			<Field as="select" name="address.Country" labelText="Country" required>
				<option value="" key="default">
					Select a Country
				</option>
				{countryOptions}
			</Field>
			<Field as="select" name="address.State" labelText="Province/State">
				<option value="" key="default">
					Select a State
				</option>
				{stateOptions}
			</Field>
			<Field name="address.Zip" labelText="Postal/Zip" required />
			<Field name="address.County" labelText="County"  />
			<Flex width={1} justifyContent="flex-end">
				<Button
					reverse
					title="Cancel"
					color={theme.gray}
					iconName="faXmark"
					onClick={() => {
						setFormToShow("");
						setEditingId(null);
					}}
				/>
				<Button
					title="Save"
					color={theme.success}
					iconName="faFloppyDisk"
					onClick={() => onSubmitAddress(values.address)}
				/>
			</Flex>
		</Flex>
	);
};

export default CreateShippingAddress;
